import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { IconX } from "@tabler/icons";
import styled, { useTheme } from "styled-components";

import { RowContainerProps } from "@sso/ui/FlexBox";
import { MenuContainer, MenuItem } from "@sso/ui/Menu";
import { RawInput } from "@sso/ui/RawInput";
import { Typography } from "@sso/ui/Typography";

import { AutoCompleteOption } from "../Formik/FormikTypeahead";

interface TypeaheadProps {
  label: string;
  items: AutoCompleteOption[];
  onChange: (value: number | string) => void;
  onBlur?: () => void;
  value?: AutoCompleteOption;
  openUpward?: boolean;
  onSearch: (searchItem: string) => void;
  heightOffset?: number;
  maxHeight?: number;
  fieldProps?: RowContainerProps;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  onClear: () => void;
}

const TypeaheadContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const ComponentContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const Typeahead = ({
  label,
  items,
  onChange,
  onBlur,
  onSearch,
  value,
  openUpward = false,
  heightOffset = 0,
  maxHeight = 250,
  error,
  errorText,
  disabled,
  fieldProps,
  onClear,
  ...containerProps
}: TypeaheadProps) => {
  const [selectedItem, setSelectedItem] = useState<AutoCompleteOption | undefined>(value);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showMenu, setShowMenu] = useState(false);
  const [inputFocussed, setInputFocussed] = useState(false);

  const containerRef = useRef(null);
  const theme = useTheme();

  const handleBlur = () => {
    onBlur && onBlur();
  };

  const handleClickOutside = useCallback(
    event => {
      if (containerRef.current) {
        const anyC = containerRef.current as any;
        if (!anyC.contains(event.target)) {
          setShowMenu(false);
          if (inputFocussed) {
            handleBlur();
          }
          setInputFocussed(false);
        }
      }
    },
    [inputFocussed, handleBlur], // Dependencies for the callback
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    onSearch(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (!selectedItem) {
      return;
    }
    setSearchTerm(selectedItem.label);
    onChange(selectedItem.value);
  }, [selectedItem]);

  const clearSearch = () => {
    setShowMenu(false);
    setSelectedItem(undefined);
    setSearchTerm("");
    onClear();
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Backspace") {
      setSelectedItem(undefined);
    }
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setSearchTerm(value);
  };

  const handleFocus = () => {
    setInputFocussed(true);
    setShowMenu(true);
  };

  useEffect(() => {
    setSearchTerm(value ? value.label : "");
  }, [value]);

  return (
    <ComponentContainer ref={containerRef}>
      <TypeaheadContainer>
        <RawInput
          onFocus={() => handleFocus()}
          value={searchTerm}
          height={"100%"}
          placeholder={label}
          onChange={onInputChange}
          error={error}
          onKeyDown={handleKeyDown}
        />
        {searchTerm && (
          <IconX onClick={clearSearch} size={20} style={{ position: "absolute", right: "0.5rem", top: "0.8rem" }} />
        )}
      </TypeaheadContainer>

      {items.length > 0 && !selectedItem && showMenu && (
        <MenuContainer maxHeight={`${maxHeight}px`} left="5px" right="5px" top={"100%"}>
          {items.map(item => (
            <MenuItem
              key={item.key}
              title={item.value.toString()}
              onClick={() => {
                setSelectedItem(item);
              }}
            >
              {item.elementLabel ?? item.label}
            </MenuItem>
          ))}
        </MenuContainer>
      )}
      {error && (
        <Typography color={theme.colors.error.main} size="0.8rem" textAlign="left" margin="0.2rem">
          {errorText}
        </Typography>
      )}
    </ComponentContainer>
  );
};

export default Typeahead;
